"use client";

import { useState, useEffect } from "react";
import { Check, ChevronRight, Globe, Moon, Sun, Monitor, Trash2, BarChart3, Volume2, Languages, X, MessageSquareText, DollarSign, Info, UserCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage, languages, targetLanguages, type Language } from "@/lib/language-context";
import { useSettings, type Theme } from "@/lib/settings-context";
import type { KnownLanguageCode, LocaleCode } from "@/lib/api/client";
import { useTranslations } from "@/lib/use-translations";
import { ScreenHeader } from "./common/screen-header";

import type { CostBreakdown } from "@/hooks/use-generation-service";

interface SettingsScreenProps {
  onBack: () => void;
  lastCost?: CostBreakdown | null;
}

type SettingsView = "main" | "native-language" | "target-language" | "known-languages";

const SHOW_COSTS = process.env.NEXT_PUBLIC_SHOW_COSTS === "true";
const FE_GIT_COMMIT = process.env.NEXT_PUBLIC_GIT_COMMIT || "dev";
const FE_GIT_TAG = process.env.NEXT_PUBLIC_GIT_TAG || "";
const FE_GIT_MESSAGE = process.env.NEXT_PUBLIC_GIT_MESSAGE || "";

export function SettingsScreen({ onBack, lastCost }: SettingsScreenProps) {
  const [view, setView] = useState<SettingsView>("main");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const { nativeLanguage, targetLanguage, setNativeLanguage, setTargetLanguage } = useLanguage();
  const { theme, setTheme, tts, setTTSSettings, analyticsOptOut, setAnalyticsOptOut, showInlineVocalizations, setShowInlineVocalizations, userProfile, setUserProfile, userDetails, setUserDetails } = useSettings();
  const t = useTranslations();

  const [backendInfo, setBackendInfo] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (!SHOW_COSTS) return;
    fetch(`${process.env.NEXT_PUBLIC_API_URL || ""}/api/info`)
      .then(r => r.ok ? r.json() : null)
      .then(setBackendInfo)
      .catch(() => {});
  }, []);

  // Toggle a language in the known languages list (stores codes like "es", "ru")
  const toggleKnownLanguage = (langCode: LocaleCode) => {
    const current: KnownLanguageCode[] = userProfile.knownLanguages || [];
    // Use base code for comparison (e.g., "en" from "en-US")
    const baseCode = langCode.split("-")[0]!.toLowerCase() as KnownLanguageCode;
    const updated: KnownLanguageCode[] = current.some(c => c.toLowerCase() === baseCode)
      ? current.filter((c) => c.toLowerCase() !== baseCode)
      : [...current, baseCode];
    setUserProfile({ ...userProfile, knownLanguages: updated });
  };

  const getBaseCode = (code: LocaleCode) => code.split("-")[0]!;

  // Get translated language name, falling back to English name
  const getTranslatedName = (langCode: LocaleCode, englishName: string) => {
    const translated = t.languageNames[langCode as keyof typeof t.languageNames];
    return translated || englishName;
  };

  const handleSelectNative = (lang: Language) => {
    // If selecting the current target language as native, swap them
    if (getBaseCode(lang.code) === getBaseCode(targetLanguage.code)) {
      const newTarget = targetLanguages.find(
        (l) => l.code === nativeLanguage.code
      );
      if (newTarget) {
        setTargetLanguage(newTarget);
      }
    }
    setNativeLanguage(lang);
    setView("main");
  };

  const handleSelectTarget = (lang: Language) => {
    // If selecting the current native language as target, swap them
    if (getBaseCode(lang.code) === getBaseCode(nativeLanguage.code)) {
      const newNative = languages.find(
        (l) => l.code === targetLanguage.code
      );
      if (newNative) {
        setNativeLanguage(newNative);
      }
    }
    setTargetLanguage(lang);
    setView("main");
  };

  const handleReset = () => {
    if (showResetConfirm) {
      localStorage.clear();
      window.location.reload();
    } else {
      setShowResetConfirm(true);
    }
  };

  // Show all languages - swap logic handles conflicts
  const nativeList = languages;
  const targetList = targetLanguages;

  const themeOptions: { value: Theme; icon: typeof Sun; label: string }[] = [
    { value: "light", icon: Sun, label: t.settings.themeLight },
    { value: "dark", icon: Moon, label: t.settings.themeDark },
    { value: "system", icon: Monitor, label: t.settings.themeSystem },
  ];

  // Language selection view
  if (view === "native-language" || view === "target-language") {
    const isNative = view === "native-language";
    const list = isNative ? nativeList : targetList;
    const current = isNative ? nativeLanguage : targetLanguage;
    const onSelect = isNative ? handleSelectNative : handleSelectTarget;

    return (
      <div className="flex flex-col min-h-screen bg-background">
        <ScreenHeader
          title={isNative ? t.languageSelector.iSpeak : t.languageSelector.iWantToLearn}
          onBack={() => setView("main")}
        />

        <div className="flex-1 overflow-y-auto">
          {list.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => onSelect(lang)}
              className={cn(
                "w-full flex items-center gap-4 px-4 py-3 border-b border-border/50 transition-colors",
                lang.code === current.code ? "bg-primary/10" : "hover:bg-muted/50"
              )}
            >
              <span className="text-2xl">{lang.flag}</span>
              <div className="flex-1 text-left">
                <p className="font-medium text-foreground">{getTranslatedName(lang.code, lang.name)}</p>
                <p className="text-sm text-muted-foreground">{lang.nativeName}</p>
              </div>
              {lang.code === current.code && (
                <Check className="w-5 h-5 text-primary" />
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Known languages selection view
  if (view === "known-languages") {
    const knownLangs = userProfile.knownLanguages || [];
    // Filter out native language - it's implied
    const selectableLanguages = languages.filter(
      (lang) => getBaseCode(lang.code) !== getBaseCode(nativeLanguage.code)
    );

    return (
      <div className="flex flex-col min-h-screen bg-background">
        <ScreenHeader
          title={t.settings.knownLanguages || "Known Languages"}
          onBack={() => setView("main")}
        />

        <div className="p-4 bg-muted/30 border-b border-border">
          <p className="text-sm text-muted-foreground">
            {t.settings.knownLanguagesDescription || "Select languages you already know. This helps us provide better pronunciation tips using sounds from these languages."}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {selectableLanguages.map((lang) => {
            // knownLangs now stores base codes (e.g., "es", "ru")
            const langBaseCode = lang.code.split("-")[0]!.toLowerCase();
            const isSelected = knownLangs.some(c => c.toLowerCase() === langBaseCode);
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => toggleKnownLanguage(lang.code)}
                className={cn(
                  "w-full flex items-center gap-4 px-4 py-3 border-b border-border/50 transition-colors",
                  isSelected ? "bg-primary/10" : "hover:bg-muted/50"
                )}
              >
                <span className="text-2xl">{lang.flag}</span>
                <div className="flex-1 text-left">
                  <p className="font-medium text-foreground">{getTranslatedName(lang.code, lang.name)}</p>
                  <p className="text-sm text-muted-foreground">{lang.nativeName}</p>
                </div>
                {isSelected && (
                  <Check className="w-5 h-5 text-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Main settings view
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <ScreenHeader title={t.settings.title} onBack={onBack} />

      <div className="flex-1 overflow-y-auto">
        {/* Languages Section */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {t.languageSelector.title}
            </h2>
          </div>

          <div className="bg-card rounded-xl border border-border overflow-hidden">
            {/* Native Language */}
            <button
              type="button"
              onClick={() => setView("native-language")}
              className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
            >
              <span className="text-2xl">{nativeLanguage.flag}</span>
              <div className="flex-1 text-left">
                <p className="text-xs text-muted-foreground">{t.languageSelector.iSpeak}</p>
                <p className="font-medium text-foreground">{getTranslatedName(nativeLanguage.code, nativeLanguage.name)}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            <div className="h-px bg-border mx-4" />

            {/* Target Language */}
            <button
              type="button"
              onClick={() => setView("target-language")}
              className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
            >
              <span className="text-2xl">{targetLanguage.flag}</span>
              <div className="flex-1 text-left">
                <p className="text-xs text-muted-foreground">{t.languageSelector.iWantToLearn}</p>
                <p className="font-medium text-foreground">{getTranslatedName(targetLanguage.code, targetLanguage.name)}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            <div className="h-px bg-border mx-4" />

            {/* Known Languages */}
            <button
              type="button"
              onClick={() => setView("known-languages")}
              className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
            >
              <Languages className="w-6 h-6 text-primary" />
              <div className="flex-1 text-left">
                <p className="text-xs text-muted-foreground">{t.settings.knownLanguages || "Known Languages"}</p>
                <p className="font-medium text-foreground">
                  {(userProfile.knownLanguages?.length || 0) > 0
                    ? (() => {
                        // Convert codes to display names
                        const displayNames = userProfile.knownLanguages!.slice(0, 3).map(code => {
                          const lang = languages.find(l => l.code.split("-")[0]!.toLowerCase() === code.toLowerCase());
                          return lang ? getTranslatedName(lang.code, lang.name) : code.toUpperCase();
                        });
                        return displayNames.join(", ") +
                          (userProfile.knownLanguages!.length > 3 ? ` +${userProfile.knownLanguages!.length - 3}` : "");
                      })()
                    : t.settings.noneSelected || "None selected"}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Learning Section */}
        <div className="p-4 pt-2">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquareText className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {t.settings.learning || "Learning"}
            </h2>
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-4">
                <p className="font-medium text-foreground">{t.settings.showPronunciationHints || "Show pronunciation hints"}</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {t.settings.showPronunciationHintsDescription || "Display how to say phrases using sounds you know"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowInlineVocalizations(!showInlineVocalizations)}
                className={cn(
                  "relative w-11 h-6 rounded-full transition-colors",
                  showInlineVocalizations ? "bg-primary" : "bg-muted"
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform",
                    showInlineVocalizations && "translate-x-5"
                  )}
                />
              </button>
            </div>
          </div>
        </div>

        {/* My Details Section - for fill-in placeholders in cue cards */}
        <div className="p-4 pt-2">
          <div className="flex items-center gap-2 mb-3">
            <UserCircle className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              My Details
            </h2>
          </div>
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            {[
              { key: "NAME", label: "Name", placeholder: "Your name" },
              { key: "PHONE", label: "Phone", placeholder: "Your phone number" },
              { key: "NIF", label: "NIF", placeholder: "Tax ID number" },
              { key: "CODE", label: "Package Code", placeholder: "Tracking code" },
            ].map((field, i) => (
              <div
                key={field.key}
                className={cn(
                  "flex items-center px-4 py-3",
                  i > 0 && "border-t border-border/50"
                )}
              >
                <label className="w-28 text-sm text-muted-foreground shrink-0">{field.label}</label>
                <input
                  type="text"
                  value={userDetails[field.key] || ""}
                  onChange={(e) => setUserDetails({ ...userDetails, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  className="flex-1 text-sm text-foreground bg-transparent outline-none placeholder:text-muted-foreground/50"
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2 px-1">
            These values fill in [NAME], [PHONE], etc. on your cue cards.
          </p>
        </div>

        {/* Appearance Section */}
        <div className="p-4 pt-2">
          <div className="flex items-center gap-2 mb-3">
            <Sun className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {t.settings.theme}
            </h2>
          </div>

          <div className="bg-card rounded-xl border border-border p-2">
            <div className="flex gap-2">
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const isActive = theme === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTheme(option.value)}
                    className={cn(
                      "flex-1 flex flex-col items-center gap-2 py-3 px-2 rounded-lg transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted/50"
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Speech Speed Section */}
        <div className="p-4 pt-2">
          <div className="flex items-center gap-2 mb-3">
            <Volume2 className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {t.settings.speechSpeed}
            </h2>
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground w-8">0.5×</span>
              <input
                type="range"
                min="0.5"
                max="1.5"
                step="0.1"
                value={tts.speed}
                onChange={(e) => setTTSSettings({ speed: parseFloat(e.target.value) })}
                className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <span className="text-xs text-muted-foreground w-8">1.5×</span>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-2">
              {tts.speed.toFixed(1)}×
            </p>
          </div>
        </div>

        {/* Analytics Section */}
        <div className="p-4 pt-2">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {t.settings.analytics}
            </h2>
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-4">
                <p className="font-medium text-foreground">{t.settings.shareUsageData}</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {t.settings.analyticsDescription}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAnalyticsOptOut(!analyticsOptOut)}
                className={cn(
                  "relative w-11 h-6 rounded-full transition-colors",
                  !analyticsOptOut ? "bg-primary" : "bg-muted"
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform",
                    !analyticsOptOut && "translate-x-5"
                  )}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Cost Breakdown (dev/staging only) */}
        {SHOW_COSTS && lastCost && (
          <div className="p-4 pt-2">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Last Generation Cost
              </h2>
            </div>

            <div className="bg-card rounded-xl border border-border p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">LLM calls</span>
                <span className="font-mono text-foreground">
                  {lastCost.llm_calls - lastCost.llm_cached} fresh + {lastCost.llm_cached} cached
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tokens</span>
                <span className="font-mono text-foreground">
                  {lastCost.llm_prompt_tokens.toLocaleString()} in / {lastCost.llm_completion_tokens.toLocaleString()} out
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">LLM cost</span>
                <span className="font-mono text-foreground">${lastCost.llm_cost_usd.toFixed(4)}</span>
              </div>
              <div className="h-px bg-border my-1" />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">TTS calls</span>
                <span className="font-mono text-foreground">
                  {lastCost.tts_calls - lastCost.tts_cached} fresh + {lastCost.tts_cached} cached ({lastCost.tts_characters.toLocaleString()} chars)
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">TTS cost</span>
                <span className="font-mono text-foreground">${lastCost.tts_cost_usd.toFixed(4)}</span>
              </div>
              {lastCost.img_calls > 0 && (
                <>
                  <div className="h-px bg-border my-1" />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Image calls</span>
                    <span className="font-mono text-foreground">
                      {lastCost.img_calls - lastCost.img_cached} fresh + {lastCost.img_cached} cached
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Image cost</span>
                    <span className="font-mono text-foreground">${lastCost.img_cost_usd.toFixed(4)}</span>
                  </div>
                </>
              )}
              {lastCost.stt_calls > 0 && (
                <>
                  <div className="h-px bg-border my-1" />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">STT calls</span>
                    <span className="font-mono text-foreground">
                      {lastCost.stt_calls} ({lastCost.stt_duration_seconds.toFixed(1)}s audio)
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">STT cost</span>
                    <span className="font-mono text-foreground">${lastCost.stt_cost_usd.toFixed(4)}</span>
                  </div>
                </>
              )}
              <div className="h-px bg-border my-1" />
              <div className="flex justify-between text-sm font-medium">
                <span className="text-foreground">Total cost</span>
                <span className="font-mono text-primary">
                  ${lastCost.total_cost_usd.toFixed(4)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Deployment Info (dev/staging only) */}
        {SHOW_COSTS && backendInfo && (
          <div className="p-4 pt-2">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Deployment Info
              </h2>
            </div>

            <div className="bg-card rounded-xl border border-border p-4 space-y-3">
              {/* Models */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Models</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Default LLM</span>
                    <span className="font-mono text-foreground text-xs">{backendInfo.default_model}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Skeleton LLM</span>
                    <span className="font-mono text-foreground text-xs">{backendInfo.skeleton_model}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Vocab Lookup</span>
                    <span className="font-mono text-foreground text-xs">{backendInfo.vocab_lookup_model}</span>
                  </div>
                </div>
              </div>

              <div className="h-px bg-border" />

              {/* Vocalizer */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Vocalizer</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Method</span>
                    <span className="font-mono text-foreground text-xs">{backendInfo.vocalization_method}</span>
                  </div>
                  {backendInfo.vocalization_method === "llm" && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Model</span>
                      <span className="font-mono text-foreground text-xs">{backendInfo.vocalization_model}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="h-px bg-border" />

              {/* Backend Git */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Backend</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Commit</span>
                    <span className="font-mono text-foreground text-xs">
                      {backendInfo.git_tag ? `${backendInfo.git_tag} / ` : ""}{backendInfo.git_commit}
                    </span>
                  </div>
                  {backendInfo.git_message && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Message</span>
                      <span className="font-mono text-foreground text-xs truncate ml-4">{backendInfo.git_message}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="h-px bg-border" />

              {/* Frontend Git */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Frontend</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Commit</span>
                    <span className="font-mono text-foreground text-xs">
                      {FE_GIT_TAG ? `${FE_GIT_TAG} / ` : ""}{FE_GIT_COMMIT}
                    </span>
                  </div>
                  {FE_GIT_MESSAGE && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Message</span>
                      <span className="font-mono text-foreground text-xs truncate ml-4">{FE_GIT_MESSAGE}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone */}
        <div className="p-4 pt-2">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="w-4 h-4 text-destructive" />
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {t.settings.resetAll}
            </h2>
          </div>

          {showResetConfirm ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground text-center px-2">
                {t.settings.resetConfirm}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(false)}
                  className="flex-1 flex items-center justify-center gap-2 p-4 bg-card rounded-xl border border-border text-foreground hover:bg-muted font-medium transition-colors"
                >
                  <X className="w-4 h-4" />
                  {t.settings.close}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-1 flex items-center justify-center gap-2 p-4 bg-destructive rounded-xl border border-destructive text-destructive-foreground hover:bg-destructive/90 font-medium transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  {t.settings.resetAll}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleReset}
              className="w-full flex items-center justify-center gap-2 p-4 bg-card rounded-xl border border-border text-destructive hover:bg-destructive/10 font-medium transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {t.settings.resetAll}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
